
import { useState } from 'react';
import Button from '../base/Button';

export default function ReservationForm() {
  const [formData, setFormData] = useState({
    nombreCompleto: '',
    email: '',
    telefono: '',
    numeroHuespedes: '',
    fechaLlegada: '',
    fechaSalida: '',
    tipoAcomodacion: '',
    transportePrivado: '',
    personasTransporte: '',
    comentarios: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar datos antes de enviar
    if (!formData.nombreCompleto || !formData.email || !formData.telefono || 
        !formData.numeroHuespedes || !formData.fechaLlegada || !formData.fechaSalida || 
        !formData.tipoAcomodacion) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Validar límite de caracteres en comentarios
    if (formData.comentarios.length > 500) {
      alert('Los comentarios no pueden exceder 500 caracteres');
      return;
    }

    setIsSubmitting(true);

    try {
      // Obtener el texto seleccionado del dropdown de huéspedes
      const numeroHuespedesSelect = document.getElementById('numeroHuespedes') as HTMLSelectElement;
      const numeroHuespedesText = numeroHuespedesSelect.options[numeroHuespedesSelect.selectedIndex].text;
      
      // Obtener el texto seleccionado del dropdown de acomodación
      const tipoAcomodacionSelect = document.getElementById('tipoAcomodacion') as HTMLSelectElement;
      const tipoAcomodacionText = tipoAcomodacionSelect.options[tipoAcomodacionSelect.selectedIndex].text;

      // Obtener información de transporte
      let transporteInfo = '';
      if (formData.transportePrivado === 'si' && formData.personasTransporte) {
        const personasTransporteSelect = document.getElementById('personasTransporte') as HTMLSelectElement;
        const personasTransporteText = personasTransporteSelect.options[personasTransporteSelect.selectedIndex].text;
        transporteInfo = `\n🚗 *Transporte privado:* Sí - ${personasTransporteText}`;
      } else if (formData.transportePrivado === 'no') {
        transporteInfo = `\n🚗 *Transporte privado:* No necesito`;
      }

      // Crear mensaje para WhatsApp
      const mensaje = `🏨 *SOLICITUD DE RESERVA - CASTILLETES TAYRONA*

👤 *Nombre:* ${formData.nombreCompleto}
📧 *Email:* ${formData.email}
📱 *Teléfono:* ${formData.telefono}
👥 *Huéspedes:* ${numeroHuespedesText}
📅 *Llegada:* ${formData.fechaLlegada}
📅 *Salida:* ${formData.fechaSalida}
🏠 *Acomodación:* ${tipoAcomodacionText}${transporteInfo}${formData.comentarios ? `\n💬 *Comentarios:* ${formData.comentarios}` : ''}

¡Hola! Me interesa hacer esta reserva. ¿Podrían confirmar disponibilidad y tarifas?`;

      // Codificar el mensaje para URL
      const mensajeCodificado = encodeURIComponent(mensaje);
      
      // Crear URL de WhatsApp
      const whatsappUrl = `https://wa.me/573196152352?text=${mensajeCodificado}`;
      
      // Abrir WhatsApp
      window.open(whatsappUrl, '_blank');

      // Limpiar formulario después del envío
      setFormData({
        nombreCompleto: '',
        email: '',
        telefono: '',
        numeroHuespedes: '',
        fechaLlegada: '',
        fechaSalida: '',
        tipoAcomodacion: '',
        transportePrivado: '',
        personasTransporte: '',
        comentarios: ''
      });

    } catch (error) {
      console.error('Error al procesar formulario:', error);
      alert('Error al procesar la solicitud. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tiposAcomodacion = [
    { value: 'jaguar-2pax', label: 'Jaguar 2 pax habitacion baño privado' },
    { value: 'zaino-2pax', label: 'ZAINO 2 pax habitacion baño compartido' },
    { value: 'caiman-6pax', label: 'CAIMAN Max 6 pax habitacion baño privado' },
    { value: 'guacamaya-2pax', label: 'Guacamaya 2 pax habitacion baño privado' },
    { value: 'cangrejo-4pax', label: 'CANGREJO 4 pax habitacion baño compartido' },
    { value: 'titi-3pax', label: 'TITI máx. 3 pax habitacion baño compartido' },
    { value: 'iguana-3pax', label: 'IGUANA 3 PAX habitacion baño compartido' },
    { value: 'tortuga-6pax', label: 'TORTUGA Max 6 PAX habitacion baño privado' },
    { value: 'delfin-8pax', label: 'DELFIN Max 8 PAX habitacion baño privado' },
    { value: 'carpa-1pax', label: 'CARPA 1 PAX COLCHONETA INDIVIDUAL' },
    { value: 'carpa-2pax', label: 'CARPA 2 PAX' },
    { value: 'carpa-3pax', label: 'CARPA 3 PAX' },
    { value: 'carpa-4pax', label: 'CARPA 4 PAX' },
    { value: 'carpa-5pax', label: 'CARPA 5 PAX' },
    { value: 'carpa-6pax', label: 'CARPA 6 PAX' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Solicitud de Reserva</h3>
        <p className="text-gray-600">Completa el formulario para enviar tu solicitud por WhatsApp</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="reservation-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="nombreCompleto" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre Completo *
            </label>
            <input
              type="text"
              id="nombreCompleto"
              name="nombreCompleto"
              value={formData.nombreCompleto}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              placeholder="Tu nombre completo"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              placeholder="tu@email.com"
            />
          </div>

          <div>
            <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono *
            </label>
            <input
              type="tel"
              id="telefono"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              placeholder="+57 300 123 4567"
            />
          </div>

          <div>
            <label htmlFor="numeroHuespedes" className="block text-sm font-medium text-gray-700 mb-1">
              Número de Huéspedes *
            </label>
            <select
              id="numeroHuespedes"
              name="numeroHuespedes"
              value={formData.numeroHuespedes}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm pr-8"
            >
              <option value="">Seleccionar</option>
              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                <option key={num} value={num}>{num} {num === 1 ? 'persona' : 'personas'}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="fechaLlegada" className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Llegada *
            </label>
            <input
              type="date"
              id="fechaLlegada"
              name="fechaLlegada"
              value={formData.fechaLlegada}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            />
          </div>

          <div>
            <label htmlFor="fechaSalida" className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Salida *
            </label>
            <input
              type="date"
              id="fechaSalida"
              name="fechaSalida"
              value={formData.fechaSalida}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="tipoAcomodacion" className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Acomodación *
          </label>
          <select
            id="tipoAcomodacion"
            name="tipoAcomodacion"
            value={formData.tipoAcomodacion}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm pr-8"
          >
            <option value="">Seleccionar tipo de acomodación</option>
            {tiposAcomodacion.map(tipo => (
              <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="transportePrivado" className="block text-sm font-medium text-gray-700 mb-1">
              ¿Necesitas transporte privado hasta el alojamiento?
            </label>
            <select
              id="transportePrivado"
              name="transportePrivado"
              value={formData.transportePrivado}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm pr-8"
            >
              <option value="">Seleccionar</option>
              <option value="si">Sí, necesito transporte</option>
              <option value="no">No, tengo transporte propio</option>
            </select>
          </div>

          {formData.transportePrivado === 'si' && (
            <div>
              <label htmlFor="personasTransporte" className="block text-sm font-medium text-gray-700 mb-1">
                Número de personas para transporte
              </label>
              <select
                id="personasTransporte"
                name="personasTransporte"
                value={formData.personasTransporte}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm pr-8"
              >
                <option value="">Seleccionar</option>
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <option key={num} value={num}>{num} {num === 1 ? 'persona' : 'personas'}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="comentarios" className="block text-sm font-medium text-gray-700 mb-1">
            Comentarios Adicionales
          </label>
          <textarea
            id="comentarios"
            name="comentarios"
            value={formData.comentarios}
            onChange={handleInputChange}
            rows={4}
            maxLength={500}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
            placeholder="Ocasión especial, preferencias alimentarias, solicitudes especiales..."
          />
          <div className="text-right text-xs text-gray-500 mt-1">
            {formData.comentarios.length}/500 caracteres
          </div>
        </div>

        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          className="w-full whitespace-nowrap"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line mr-2 animate-spin"></i>
              Procesando...
            </>
          ) : (
            <>
              <i className="ri-whatsapp-line mr-2"></i>
              Enviar por WhatsApp
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
